<template>
    <div>
        <v-container class="pa-5" fluid>
            <ConfirmDialog
                :is-open="deleteTeamDialog"
                :data="currentTeam"
                @on-confirm="deleteTeam"
                @on-abort="closeDeleteTeamDialog"
            >
                <template #title>
                    Usuwanie ekipy
                </template>
                <template #content>
                    Czy na pewno chcesz usunąć ekipę?
                </template>
                <template #confirmButton>
                    Usuń
                </template>
            </ConfirmDialog>

            <v-row align="center" dense>
                <v-col cols="auto">
                    <page-title>Ekipy</page-title>
                </v-col>
                <v-col cols="auto">
                    <SelectMenu />
                </v-col>
            </v-row>

            <v-row align="center">
                <v-col cols="12" lg="4">
                    <v-btn
                        color="secondary"
                        medium
                        data-sentry-label="Dodaj ekipę"
                        @click="createTeam"
                    >
                        <v-icon left>
                            mdi-plus
                        </v-icon>
                        Dodaj ekipę
                    </v-btn>
                </v-col>
                <v-col cols="12" sm="8">
                    <v-container fluid class="pa-0">
                        <v-row justify="end" dense>
                            <v-col cols="12" md="8" lg="4">
                                <v-text-field
                                    v-model="filters.name"
                                    append-icon="mdi-magnify"
                                    label="Szukaj..."
                                    single-line
                                    clearable
                                    hide-details
                                    solo
                                    @keyup.enter="changeFilter"
                                    @click:append="changeFilter"
                                    @click:clear="clearFilter('name')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-card>
                        <v-data-table
                            :headers="headers"
                            :items="teams"
                            :loading="teamsLoading"
                            :footer-props="footerProps"
                            :server-items-length="pagination.itemsLength"
                            :mobile-breakpoint="0"
                            :item-class="setSelectedRow"
                            name="teamsData"
                            @pagination="updatePagination"
                        >
                            <template #item.name="{item}">
                                {{ item.name }}
                            </template>
                            <template #item.contactMail="{item}">
                                {{ item.contactMail }}
                            </template>
                            <template #item.street="{item}">
                                {{ item.street }}
                            </template>
                            <template #item.postCode="{item}">
                                {{ item.postCode }}
                            </template>
                            <template #item.city="{item}">
                                {{ item.city }}
                            </template>
                            <template #item.coordinates="{item}">
                                <v-icon
                                    v-if="item.latitude && item.longitude"
                                    small
                                    color="success"
                                >
                                    mdi-check
                                </v-icon>
                                <v-icon v-else small color="error">
                                    mdi-close
                                </v-icon>
                            </template>
                            <template #item.actions="{item}">
                                <div class="d-inline-flex">
                                    <v-tooltip :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <v-btn
                                                icon
                                                data-sentry-label="Edytuj"
                                                v-on="on"
                                                @click="openTeam(item.id)"
                                            >
                                                <v-icon color="blue">
                                                    mdi-ballot
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Edytuj</span>
                                    </v-tooltip>
                                    <v-tooltip top>
                                        <template #activator="{on}">
                                            <v-btn
                                                icon
                                                data-sentry-label="Usuń"
                                                v-on="on"
                                                @click="showDeleteTeamDialog(item)"
                                            >
                                                <v-icon color="pink">
                                                    mdi-delete
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Usuń</span>
                                    </v-tooltip>
                                </div>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import SelectMenu from '@/views/Outdoors/Common/SelectMenu'
import filters from '@/mixins/dataTableFilters'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'OutdoorsList',
    components: {
        PageTitle,
        ConfirmDialog,
        SelectMenu
    },
    mixins: [
        pagination,
        tooltipDelay,
        filters,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('outdoors/outdoorsSettings/clearTeamFilters')
            this.clearSelectedListItemId()
        }
        next()
    },
    data() {
        return {
            currentTeam: {
                name: null,
                contactPerson: null,
                city: null,
                street: null,
                postCode: null,
                contactMail: null,
                phoneNumber: null,
                contractorId: null
            },
            deleteTeamDialog: false,
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    sortable: false
                },
                {
                    text: 'Email',
                    value: 'contactMail',
                    sortable: false
                },
                {
                    text: 'Adres',
                    value: 'street',
                    sortable: false,
                },
                {
                    text: 'Kod pocztowy',
                    value: 'postCode',
                    sortable: false
                },
                {
                    text: 'Miasto',
                    value: 'city',
                    sortable: false,
                },
                {
                    text: 'Współrzędne',
                    value: 'coordinates',
                    sortable: false,
                    align: 'center',
                    width: 80
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        teams() {
            return this.$store.state.outdoors.outdoorsSettings.outdoorsLocalizationTeams.outdoorsLocalizationTeams
        },
        teamsLoading() {
            return this.$store.state.outdoors.outdoorsSettings.outdoorsLocalizationTeams.outdoorsLocalizationTeamsLoading
        },
        savedFilters() {
            return this.$store.state.outdoors.outdoorsSettings.outdoorsLocalizationTeams.filters
        },
        selectedTeamId() {
            return this.$store.state.common.selectedListItemId
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.getTeams()
    },
    methods: {
        setSelectedRow(item) {
            if (item.id === this.selectedTeamId) {
                return 'isSelected'
            }
        },
        getTeams() {
            this.$store.dispatch('outdoors/outdoorsSettings/getTeams', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createTeam() {
            this.$router.push({
                name: 'outdoorsSettingsOutdoorsTeamsOutdoorsTeamCreate',
                params: {
                    isFromOutdoorsTeams: true
                }
            })
                .catch(error => errorHandler(error))
        },
        openTeam(teamId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(teamId)
            this.$router.push({
                name: 'outdoorsSettingsOutdoorsTeamsOutdoorsTeamEdit',
                params: {
                    id: teamId,
                    isFromOutdoorsTeams: true
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showDeleteTeamDialog(team) {
            this.currentTeam = team
            this.deleteTeamDialog = true
        },
        closeDeleteTeamDialog() {
            this.deleteTeamDialog = false

            setTimeout(() => {
                this.currentTeam = {}
            }, 500)
        },
        deleteTeam(team) {
            this.actionLoading = true

            this.$store.dispatch('outdoors/outdoorsSettings/deleteTeam', team.id)
                .then(() => {
                    this.getTeams()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeleteTeamDialog()
                })
        },
        changeFilter() {
            this.$store.dispatch('outdoors/outdoorsSettings/setTeamFilters', this.filters)
            this.resetPagination()
            this.getTeams()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getTeams()
            }
            this.clearPaginationPending()
        }
    }
}
</script>

